import React from 'react'
import { X, Plus } from 'lucide-react'
import type { ActionCard, Card, ControlCard } from './engine'
import { nextId } from './engine'
import { getCardDef, controlParamLabel } from './cardDefs'
import {
  getDrag,
  setDrag,
  createDragGhost,
  moveDragGhost,
  destroyDragGhost,
  triggerDropAtPoint,
  registerDropZone,
  type DragPayload,
} from './dnd'

export interface TargetOption {
  id: string
  name: string
  potatoes: number
}

interface PirateCardProps {
  card: Card
  editable: boolean
  targets?: TargetOption[]
  onChange: (updated: Card) => void
  onRemove: () => void
  highlightCardId?: string | null
  compact?: boolean
  shipSlotsUsed?: number
  /** When false, hides the param dropdown and shows the value as static text. */
  allowParamEdit?: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeActionCard(type: string): ActionCard {
  return { id: nextId('card'), kind: 'action', action: type as ActionCard['action'], targetShipId: null }
}

function makeControlCard(type: string, param?: number): ControlCard {
  const def = getCardDef(type as any)
  return {
    id: nextId('card'),
    kind: 'control',
    control: type as ControlCard['control'],
    param: param ?? def.paramOptions?.[0] ?? 0,
    body: [],
    ifBody: [],
    elseBody: [],
  }
}

function selectStyle(border: string): React.CSSProperties {
  return { fontSize: 11, fontWeight: 600, padding: '2px 4px', borderRadius: 6, border: `1px solid ${border}`, background: 'var(--color-input-bg)', color: 'var(--color-text)', cursor: 'pointer' }
}

function staticParamStyle(border: string): React.CSSProperties {
  return { fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: 'var(--color-text)', display: 'inline-block' }
}

// ---------------------------------------------------------------------------
// DropZone — shared empty slot indicator
// ---------------------------------------------------------------------------
function DropZone({ editable, over, onOver, onLeave, onDrop, hint }: {
  editable: boolean; over: boolean
  onOver: (e: React.DragEvent) => void; onLeave: () => void; onDrop: (e: React.DragEvent) => void
  hint?: string
}) {
  return (
    <div
      onDragOver={onOver} onDragEnter={onOver} onDragLeave={onLeave} onDrop={onDrop}
      style={{
        border: `2px dashed ${over ? 'var(--pp-action-accent)' : 'var(--pp-border)'}`, borderRadius: 8, padding: '8px 10px',
        fontSize: 11, textAlign: 'center', color: over ? 'var(--pp-action-accent)' : 'var(--pp-text-muted)',
        background: over ? 'var(--color-primary-subtle)' : 'transparent',
      }}
    >
      {editable ? (hint ?? 'Drop a card here') : '(empty)'}
    </div>
  )
}

// ---------------------------------------------------------------------------
// BodySlot — multi-card body for For / While cards.
// Accepts: action cards + for/while control cards. Rejects ifelse.
// Allows mixing (e.g. [roast, for×2(fry)]). Capacity-gated by shipSlotsUsed.
// Used recursively: a for/while inside a body also gets a BodySlot.
// ---------------------------------------------------------------------------
function BodySlot({ body, editable, canAdd, shipSlotsUsed, targets, onSet, highlightCardId, allowParamEdit = true }: {
  body: Card[]; editable: boolean; canAdd: boolean; shipSlotsUsed: number
  targets?: TargetOption[]; onSet: (b: Card[]) => void; highlightCardId?: string | null
  allowParamEdit?: boolean
}) {
  const [over, setOver] = React.useState(false)
  const hasControl = body.some((c) => c.kind === 'control')
  const hasAction = body.some((c) => c.kind === 'action')
  // A nested loop locks the body — no sibling cards allowed (same rule as at ship level).
  const full = hasControl || body.length >= 2 || !canAdd

  const dropZoneRef = React.useRef<HTMLDivElement>(null)
  const dropHandlerRef = React.useRef<() => void>(() => {})
  dropHandlerRef.current = () => {
    const p = getDrag()
    if (!editable || !p || full || p.type === 'ifelse') return
    if (p.kind === 'action') {
      if (hasControl) return
      onSet([...body, makeActionCard(p.type)])
    } else {
      if (hasAction || hasControl) return
      onSet([makeControlCard(p.type, p.param)])
    }
    setOver(false)
  }
  React.useEffect(() => {
    const el = dropZoneRef.current
    if (!el) return
    return registerDropZone(el, () => dropHandlerRef.current())
  }, [])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dropHandlerRef.current()
  }

  const accept = (e: React.DragEvent) => {
    const p = getDrag()
    if (!editable || !p || full || p.type === 'ifelse') return
    const ok = (p.kind === 'action' && !hasControl) || (p.kind === 'control' && !hasAction && !hasControl)
    if (ok) { e.preventDefault(); e.stopPropagation(); setOver(true) }
  }

  const updateAt = (idx: number, c: Card) => { const n = body.slice(); n[idx] = c; onSet(n) }
  const removeAt = (idx: number) => onSet(body.filter((_, i) => i !== idx))

  return (
    <div
      ref={dropZoneRef}
      onDragOver={accept} onDragEnter={accept} onDragLeave={() => setOver(false)} onDrop={handleDrop}
      style={{
        border: `2px dashed ${over ? 'var(--pp-action-accent)' : body.length > 0 ? 'transparent' : 'var(--pp-border)'}`,
        borderRadius: 8, background: over ? 'var(--color-primary-subtle)' : 'transparent',
        display: 'flex', flexDirection: 'column', gap: 4,
        minHeight: body.length === 0 ? 36 : undefined, transition: 'background 100ms',
      }}
    >
      {body.length === 0 && (
        <DropZone
          editable={editable && !full} over={over}
          onOver={accept} onLeave={() => setOver(false)} onDrop={handleDrop}
          hint={!canAdd ? '(ship full)' : 'Drop attack or For/While'}
        />
      )}

      {body.map((item, idx) => (
        item.kind === 'action' ? (
          <PirateCard
            key={item.id} card={item} editable={editable} targets={targets}
            onChange={(c) => updateAt(idx, c)} onRemove={() => removeAt(idx)}
            highlightCardId={highlightCardId} compact allowParamEdit={allowParamEdit}
          />
        ) : (
          <NestedControlCard
            key={item.id} card={item as ControlCard} editable={editable}
            shipSlotsUsed={shipSlotsUsed} targets={targets}
            onChange={(c) => updateAt(idx, c as Card)} onRemove={() => removeAt(idx)}
            highlightCardId={highlightCardId} allowParamEdit={allowParamEdit}
          />
        )
      ))}

      {/* Second drop slot only when 1 action card is present — loops are always alone */}
      {body.length === 1 && hasAction && editable && !full && (
        <div
          onDragOver={accept} onDragEnter={accept} onDragLeave={() => setOver(false)} onDrop={handleDrop}
          style={{
            border: `2px dashed ${over ? 'var(--pp-action-accent)' : 'var(--pp-border)'}`, borderRadius: 8,
            padding: '5px 8px', fontSize: 10, textAlign: 'center',
            color: over ? 'var(--pp-action-accent)' : 'var(--pp-text-muted)', background: over ? 'var(--color-primary-subtle)' : 'transparent',
          }}
        >
          <Plus size={10} style={{ display: 'inline', marginRight: 3 }} />
          stack 2nd card
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// NestedControlCard — a for/while card rendered inside another card's body slot.
// Has its own BodySlot so nesting works recursively up to the 3-card limit.
// ---------------------------------------------------------------------------
function NestedControlCard({ card, editable, shipSlotsUsed, targets, onChange, onRemove, highlightCardId, allowParamEdit = true }: {
  card: ControlCard; editable: boolean; shipSlotsUsed: number
  targets?: TargetOption[]
  onChange: (c: ControlCard) => void; onRemove: () => void; highlightCardId?: string | null
  allowParamEdit?: boolean
}) {
  const def = getCardDef(card.control)
  const Icon = def.icon
  const highlighted = highlightCardId === card.id

  return (
    <div style={{
      background: def.bg, border: `2px solid ${def.border}`, borderRadius: 10, padding: '8px 10px',
      color: def.text,
      boxShadow: highlighted ? `0 0 0 3px ${def.accent}` : '0 1px 2px rgba(0,0,0,0.08)',
      transform: highlighted ? 'scale(1.04)' : 'none', transition: 'transform 120ms ease, box-shadow 120ms ease',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: def.accent, color: '#fff', flexShrink: 0 }}>
          <Icon size={14} />
        </span>
        <span style={{ fontWeight: 800, fontSize: 12, flex: 1 }}>{def.label}</span>
        {editable && (
          <button onClick={(e) => { e.stopPropagation(); onRemove() }} title="Remove"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: def.text, opacity: 0.6, padding: 0, display: 'inline-flex' }}>
            <X size={13} />
          </button>
        )}
      </div>
      {/* Param */}
      <div style={{ marginTop: 5 }}>
        {allowParamEdit ? (
          <select value={card.param} disabled={!editable}
            onChange={(e) => onChange({ ...card, param: Number(e.target.value) })}
            onClick={(e) => e.stopPropagation()} style={selectStyle(def.border)}>
            {def.paramOptions?.map((p) => (
              <option key={p} value={p}>{controlParamLabel(card.control as 'for' | 'while', p)}</option>
            ))}
          </select>
        ) : (
          <span style={staticParamStyle(def.border)}>{controlParamLabel(card.control as 'for' | 'while', card.param)}</span>
        )}
      </div>
      {/* Body */}
      <div style={{ marginTop: 7 }}>
        <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, marginBottom: 3 }}>BODY</div>
        <BodySlot
          body={card.body ?? []} editable={editable} canAdd={shipSlotsUsed < 3}
          shipSlotsUsed={shipSlotsUsed} targets={targets}
          onSet={(b) => onChange({ ...card, body: b })} highlightCardId={highlightCardId}
          allowParamEdit={allowParamEdit}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// IfElseBranchSlot — one IF or ELSE branch of an if/else card.
// Accepts: action cards + for/while cards. Rejects ifelse.
// No mixing restriction — canAdd gating enforces the 3-slot ship limit.
// ---------------------------------------------------------------------------
function IfElseBranchSlot({ label, branch, editable, canAdd, shipSlotsUsed, onSet, highlightCardId, allowParamEdit = true }: {
  label: string; branch: Card[]; editable: boolean; canAdd: boolean; shipSlotsUsed: number
  onSet: (b: Card[]) => void; highlightCardId?: string | null
  allowParamEdit?: boolean
}) {
  const [over, setOver] = React.useState(false)
  const hasControl = branch.some((c) => c.kind === 'control')
  const hasAction = branch.some((c) => c.kind === 'action')
  // Branch is full: has a control card (body inside it), or 2 actions, or ship full
  const branchFull = hasControl || branch.length >= 2 || !canAdd

  const dropZoneRef = React.useRef<HTMLDivElement>(null)
  const dropHandlerRef = React.useRef<() => void>(() => {})
  dropHandlerRef.current = () => {
    const p = getDrag()
    if (!editable || !p || branchFull || p.type === 'ifelse') return
    if (p.kind === 'action') {
      if (hasControl) return
      onSet([...branch, makeActionCard(p.type)])
    } else {
      if (hasAction || hasControl) return
      onSet([makeControlCard(p.type, p.param)])
    }
    setOver(false)
  }
  React.useEffect(() => {
    const el = dropZoneRef.current
    if (!el) return
    return registerDropZone(el, () => dropHandlerRef.current())
  }, [])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dropHandlerRef.current()
  }

  const accept = (e: React.DragEvent) => {
    const p = getDrag()
    if (!editable || !p || branchFull || p.type === 'ifelse') return
    const ok = (p.kind === 'action' && !hasControl) || (p.kind === 'control' && !hasAction && !hasControl)
    if (ok) { e.preventDefault(); e.stopPropagation(); setOver(true) }
  }

  const updateAt = (idx: number, c: Card) => { const n = branch.slice(); n[idx] = c; onSet(n) }
  const removeAt = (idx: number) => onSet(branch.filter((_, i) => i !== idx))
  const labelColour = label === 'IF' ? 'var(--pp-action-accent)' : 'var(--color-danger)'

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, color: labelColour, marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      <div
        ref={dropZoneRef}
        onDragOver={accept} onDragEnter={accept} onDragLeave={() => setOver(false)} onDrop={handleDrop}
        style={{
          border: `2px dashed ${over ? 'var(--pp-action-accent)' : branch.length > 0 ? 'transparent' : 'var(--pp-border)'}`,
          borderRadius: 8, background: over ? 'var(--color-primary-subtle)' : 'transparent',
          display: 'flex', flexDirection: 'column', gap: 4,
          minHeight: branch.length === 0 ? 36 : undefined, transition: 'background 100ms',
        }}
      >
        {branch.length === 0 && (
          <DropZone
            editable={editable && !branchFull} over={over}
            onOver={accept} onLeave={() => setOver(false)} onDrop={handleDrop}
            hint={!canAdd ? '(ship full)' : 'Drop attack or For/While'}
          />
        )}

        {branch.map((item, idx) => (
          item.kind === 'action' ? (
            <PirateCard
              key={item.id} card={item} editable={editable}
              onChange={(c) => updateAt(idx, c)} onRemove={() => removeAt(idx)}
              highlightCardId={highlightCardId} compact allowParamEdit={allowParamEdit}
              // No targets — if/else picks the target ship
            />
          ) : (
            <NestedControlCard
              key={item.id} card={item as ControlCard} editable={editable}
              shipSlotsUsed={shipSlotsUsed}
              onChange={(c) => updateAt(idx, c as Card)} onRemove={() => removeAt(idx)}
              highlightCardId={highlightCardId} allowParamEdit={allowParamEdit}
            />
          )
        ))}

        {branch.length === 1 && hasAction && editable && !branchFull && (
          <div
            onDragOver={accept} onDragEnter={accept} onDragLeave={() => setOver(false)} onDrop={handleDrop}
            style={{
              border: `2px dashed ${over ? 'var(--pp-action-accent)' : 'var(--pp-border)'}`, borderRadius: 8,
              padding: '5px 8px', fontSize: 10, textAlign: 'center',
              color: over ? 'var(--pp-action-accent)' : 'var(--pp-text-muted)', background: over ? 'var(--color-primary-subtle)' : 'transparent',
            }}
          >
            <Plus size={10} style={{ display: 'inline', marginRight: 3 }} />
            stack 2nd attack
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main PirateCard
// ---------------------------------------------------------------------------
export function PirateCard({ card, editable, targets, onChange, onRemove, highlightCardId, compact = false, shipSlotsUsed = 0, allowParamEdit = true }: PirateCardProps) {
  const def = getCardDef(card.kind === 'action' ? card.action : card.control)
  const Icon = def.icon
  const highlighted = highlightCardId === card.id

  const targetSelect = card.kind === 'action' && targets && targets.length > 0 ? (
    <select value={card.targetShipId ?? ''} disabled={!editable}
      onChange={(e) => onChange({ ...card, targetShipId: e.target.value || null })}
      onClick={(e) => e.stopPropagation()} style={selectStyle(def.border)}>
      <option value="">🎯 Auto (first enemy)</option>
      {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  ) : null

  return (
    <div draggable={false} style={{
      background: def.bg, border: `2px solid ${def.border}`, borderRadius: 10,
      padding: compact ? '6px 8px' : '8px 10px', color: def.text,
      boxShadow: highlighted ? `0 0 0 3px ${def.accent}` : '0 1px 2px rgba(0,0,0,0.08)',
      transform: highlighted ? 'scale(1.04)' : 'none', transition: 'transform 120ms ease, box-shadow 120ms ease',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: compact ? 22 : 26, height: compact ? 22 : 26, borderRadius: 6, background: def.accent, color: '#fff', flexShrink: 0 }}>
          <Icon size={compact ? 14 : 16} />
        </span>
        <span style={{ fontWeight: 800, fontSize: compact ? 12 : 13, flex: 1 }}>{def.label}</span>
        {card.kind === 'action' && <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.8 }}>−{def.damage}🥔</span>}
        {editable && (
          <button onClick={(e) => { e.stopPropagation(); onRemove() }} title="Remove card"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: def.text, opacity: 0.6, padding: 0, display: 'inline-flex' }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Control param */}
      {card.kind === 'control' && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {allowParamEdit ? (
            <select value={card.param} disabled={!editable}
              onChange={(e) => onChange({ ...card, param: Number(e.target.value) } as ControlCard)}
              onClick={(e) => e.stopPropagation()} style={selectStyle(def.border)}>
              {def.paramOptions?.map((p) => <option key={p} value={p}>{controlParamLabel(card.control, p)}</option>)}
            </select>
          ) : (
            <span style={staticParamStyle(def.border)}>{controlParamLabel(card.control, card.param)}</span>
          )}
        </div>
      )}

      {/* Target selector (action cards only) */}
      {card.kind === 'action' && targetSelect && <div style={{ marginTop: 6 }}>{targetSelect}</div>}

      {/* Nested slots */}
      {card.kind === 'control' && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {card.control === 'ifelse' ? (
            <>
              <IfElseBranchSlot
                label="IF" branch={card.ifBody ?? []} editable={editable}
                canAdd={shipSlotsUsed < 3} shipSlotsUsed={shipSlotsUsed}
                onSet={(b) => onChange({ ...card, ifBody: b } as ControlCard)}
                highlightCardId={highlightCardId} allowParamEdit={allowParamEdit}
              />
              <IfElseBranchSlot
                label="ELSE" branch={card.elseBody ?? []} editable={editable}
                canAdd={shipSlotsUsed < 3} shipSlotsUsed={shipSlotsUsed}
                onSet={(b) => onChange({ ...card, elseBody: b } as ControlCard)}
                highlightCardId={highlightCardId} allowParamEdit={allowParamEdit}
              />
            </>
          ) : (
            // for / while: multi-card body slot
            <>
              <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, marginBottom: -2 }}>BODY</div>
              <BodySlot
                body={card.body ?? []} editable={editable} canAdd={shipSlotsUsed < 3}
                shipSlotsUsed={shipSlotsUsed} targets={targets}
                onSet={(b) => onChange({ ...card, body: b } as ControlCard)}
                highlightCardId={highlightCardId} allowParamEdit={allowParamEdit}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Palette card
// ---------------------------------------------------------------------------
interface PaletteCardProps extends DragPayload {
  overrideLabel?: string
  overrideDescription?: string
}

export function PaletteCard({ type, kind, param, overrideLabel, overrideDescription }: PaletteCardProps) {
  const def = getCardDef(type)
  const Icon = def.icon
  const label = overrideLabel ?? def.label
  const description = overrideDescription ?? def.description
  const ref = React.useRef<HTMLDivElement>(null)

  // Non-passive touchmove to prevent scroll while dragging
  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const onTouchMove = (e: TouchEvent) => {
      if (getDrag()) {
        e.preventDefault()
        moveDragGhost(e.touches[0].clientX, e.touches[0].clientY)
      }
    }
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [])

  const onTouchStart = (e: React.TouchEvent) => {
    setDrag({ type, kind, param })
    createDragGhost(label, def.accent)
    moveDragGhost(e.touches[0].clientX, e.touches[0].clientY)
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0]
    destroyDragGhost()
    triggerDropAtPoint(t.clientX, t.clientY)
    setDrag(null)
  }

  return (
    <div
      ref={ref}
      draggable
      onDragStart={() => setDrag({ type, kind, param })}
      onDragEnd={() => setDrag(null)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      title={description}
      style={{ background: def.bg, border: `2px solid ${def.border}`, borderRadius: 10, padding: '8px 10px', cursor: 'grab', color: def.text, userSelect: 'none', touchAction: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, background: def.accent, color: '#fff' }}>
          <Icon size={16} />
        </span>
        <span style={{ fontWeight: 800, fontSize: 13, flex: 1 }}>{label}</span>
        {def.kind === 'action' && <span style={{ fontSize: 11, fontWeight: 700 }}>−{def.damage}🥔</span>}
      </div>
      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.75, lineHeight: 1.3 }}>{description}</div>
    </div>
  )
}
